import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { redactUrl } from '@core/telemetry/telemetry-redaction.util';

// A marker only explains the requests an action fires more or less immediately. Past this window a
// stale label would mis-attribute unrelated background traffic to the last thing the user clicked.
const MARKER_TTL_MS = 8_000;

const MAX_LABEL_LENGTH = 80;

interface DevActionMarker {
  readonly label: string;
  readonly expiresAt: number;
}

/**
 * Holds the short-lived label answering "what did the user just do?", which
 * `devAttributionInterceptor` sends as `X-Dev-Trigger` so the ProVet diagnostics panel can tie a
 * burst of provider calls back to its cause.
 *
 * Developer-tooling only: nothing outside `enableDevTools` builds reads it.
 */
@Injectable({ providedIn: 'root' })
export class DevActionMarkerService {
  private readonly router = inject(Router);

  private marker: DevActionMarker | null = null;

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) this.mark(redactUrl(event.url));
    });
  }

  public mark(label: string, ttlMs: number = MARKER_TTL_MS): void {
    const trimmed = label.trim();
    if (!trimmed) return;

    this.marker = {
      label: trimmed.slice(0, MAX_LABEL_LENGTH),
      expiresAt: Date.now() + ttlMs,
    };
  }

  public clear(): void {
    this.marker = null;
  }

  public currentLabel(): string | null {
    if (!this.marker) return null;
    if (Date.now() > this.marker.expiresAt) {
      this.marker = null;
      return null;
    }

    return this.marker.label;
  }
}
